import { create } from "zustand";
import Cookies from "js-cookie";
import api from "../pages/chat/api";
import { INTROS, TOURS } from "./tours";

const ENDPOINT = "api/v1/accounts/onboarding/";

const currentUserId = () => {
  try {
    return JSON.parse(atob(Cookies.get("access_token").split(".")[1])).user_id ?? null;
  } catch (err) {
    return null;
  }
};

const mergeSections = (current, patch) => {
  const next = { ...current };
  Object.keys(patch).forEach((section) => {
    next[section] = { ...(current[section] || {}), ...patch[section] };
  });
  return next;
};

// `state` stays null for accounts that signed up before the tour existed, and
// every piece of onboarding UI renders nothing while it is null.
export const useOnboarding = create((set, get) => ({
  loaded: false,
  state: null,
  profile: { fullName: "", role: "", useCase: "" },
  activeTour: null,
  toursMenuOpen: false,
  forcedIntro: null,
  celebration: null,
  toast: null,
  sessionSkipped: [],

  userKey: null,

  // Layout remounts on some route changes, so only a different account resets on-screen state.
  load: async () => {
    const userKey = Cookies.get("access_token") ? currentUserId() : null;
    if (userKey !== get().userKey) {
      set({ userKey, loaded: false, state: null, activeTour: null, celebration: null, toast: null, forcedIntro: null, sessionSkipped: [] });
    }
    try {
      const { data } = await api.get(ENDPOINT);
      const server = data?.onboarding || null;
      const local = get().state;
      // A save still in flight is newer than what the server just returned.
      set({
        loaded: true,
        state: server && local ? mergeSections(server, local) : server,
        profile: { fullName: data?.full_name || "", role: data?.role || "", useCase: data?.use_case || "" },
      });
    } catch (err) {
      set({ loaded: true });
    }
  },

  save: (patch) => {
    const current = get().state;
    if (!current) return;
    set({ state: mergeSections(current, patch) });
    api.patch(ENDPOINT, patch).catch(() => {});
  },

  isSeen: (key) => !!get().state?.seen?.[key],
  isDone: (key) => !!get().state?.done?.[key],

  markSeen: (key) => get().save({ seen: { [key]: true } }),

  markDone: (key, extra = {}) => {
    const { state, save } = get();
    if (!state || state.done?.[key]) return;
    save({ done: { [key]: true } });
    if (key === "project") set({ celebration: { projectName: extra.name || "" } });
  },

  setChecklist: (patch) => get().save({ checklist: patch }),

  startTour: (id) => {
    if (!TOURS[id]) return;
    set({ activeTour: { id, step: 0 }, toursMenuOpen: false, forcedIntro: null });
    // Taking a page's tour covers its intro, so the intro must not pop up when the tour ends.
    const introKey = Object.keys(INTROS).find((key) => INTROS[key].tour === id);
    if (introKey && !get().state?.seen?.[introKey]) get().markSeen(introKey);
  },
  goToStep: (step) => {
    const tour = get().activeTour;
    if (tour) set({ activeTour: { ...tour, step } });
  },
  endTour: (completed) => {
    const tour = get().activeTour;
    if (!tour) return;
    set({ activeTour: null });
    if (completed) get().markSeen(TOURS[tour.id].seenKey);
    if (completed && tour.id === "app") {
      get().save({ done: { tour: true } });
      set({ toast: "Tour complete. You can now start using DocuSensa." });
    }
  },

  skipForSession: (introKey) => set({ sessionSkipped: [...get().sessionSkipped, introKey] }),
  setToursMenuOpen: (open) => set({ toursMenuOpen: open }),
  showIntro: (introKey) => set({ forcedIntro: introKey, toursMenuOpen: false }),
  clearForcedIntro: () => set({ forcedIntro: null }),
  closeCelebration: () => set({ celebration: null }),
  clearToast: () => set({ toast: null }),
}));

// For the places in the app that complete a checklist step. A no-op for
// accounts that are not in onboarding, so callers never need to check.
export const markOnboarding = (key, extra) => useOnboarding.getState().markDone(key, extra);
